import * as z from 'zod'
import {
  type CustomCell,
  type CustomRenderer,
  GridCellKind,
  getMiddleCenterBias,
  roundedRect,
} from '@glideapps/glide-data-grid'
import { drawImage } from '../draw-fns'
import { CELL_BUTTON_SIZE, SPRITE_SIZE } from '../theme'
import {
  getSquareBB,
  getSquareXPosFromAlign,
  isOverEditableRegion,
  pointIsWithinBB,
} from './utils'

export const svgCellSchema = z.object({
  kind: z.literal('svg-cell'),
  value: z.string(),
  onClick: z.function(z.tuple([z.object({ metaKey: z.boolean() })])),
})

type SvgCellProps = z.infer<typeof svgCellSchema>

function encodeSvgToBase64(svg: string): string {
  const encoded = encodeURIComponent(svg)
    .replace(/'/g, '%27')
    .replace(/"/g, '%22')
  return `data:image/svg+xml;charset=utf-8,${encoded}`
}

export type SvgCell = CustomCell<SvgCellProps>

export const SvgCellRenderer: CustomRenderer<SvgCell> = {
  kind: GridCellKind.Custom,
  isMatch: (cell: CustomCell): cell is SvgCell =>
    (cell.data as any).kind === 'svg-cell',
  needsHover: true,
  needsHoverPosition: true,
  draw: (args) => {
    const { cell, rect } = args

    // Convert SVG to data URI
    const dataUri = encodeSvgToBase64(cell.data.value)

    drawImage(args, [dataUri], 0, cell.contentAlign)

    const bb = getSquareBB(
      rect.x,
      Math.floor(rect.y + rect.height / 2),
      rect.width * 2
    )
    const hovered =
      args.hoverX && args.hoverY
        ? pointIsWithinBB(rect.x + args.hoverX, rect.y + args.hoverY, bb)
        : false

    if (!hovered && !args.highlighted) return true
    const { ctx, hoverX, hoverY, theme } = args

    const yPad = theme.cellVerticalPadding
    const posX = getSquareXPosFromAlign(
      'right',
      rect.x,
      rect.width,
      yPad,
      CELL_BUTTON_SIZE
    )

    const centerY = rect.y + rect.height / 2 + getMiddleCenterBias(ctx, theme)
    const bbAction = getSquareBB(posX, centerY, CELL_BUTTON_SIZE)
    const hasHoveredLink =
      hoverX && hoverY
        ? pointIsWithinBB(rect.x + hoverX, rect.y + hoverY, bbAction)
        : false

    // draw button arrow
    roundedRect(
      ctx,
      posX - CELL_BUTTON_SIZE / 2,
      centerY - CELL_BUTTON_SIZE / 2,
      CELL_BUTTON_SIZE,
      CELL_BUTTON_SIZE,
      6
    )
    ctx.fillStyle = hasHoveredLink ? theme.bgHeaderHovered : theme.bgHeader
    ctx.fill()

    // draw button stroke
    ctx.lineWidth = 1.2
    ctx.strokeStyle = hovered
      ? theme.bgBubble
      : theme.headerBottomBorderColor ?? '#252525'
    ctx.stroke()

    args.spriteManager.drawSprite(
      'arrow-top-right',
      'normal',
      ctx,
      posX - SPRITE_SIZE / 2,
      centerY - SPRITE_SIZE / 2,
      SPRITE_SIZE,
      theme
    )

    return true
  },
  provideEditor: () => undefined,
  onClick: (e) => {
    const clickedAction = isOverEditableRegion(e)
    if (!clickedAction) {
      return undefined
    }

    // const isPressingMeta = e.event.metaKey || e.event.ctrlKey
    e.preventDefault()
    e.cell.data.onClick({ metaKey: e.metaKey })
  },
}
